import * as React from 'react'
import type { DayButton } from 'react-day-picker'
import { DayPicker, getDefaultClassNames } from 'react-day-picker'

import { cn } from '#/shared/lib/utils'
import { Button, buttonVariants } from '#/shared/ui/button'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '#/shared/ui/icons'

type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
}

function buildClassNames(
  buttonVariant: CalendarProps['buttonVariant'],
  captionLayout: CalendarProps['captionLayout'],
) {
  const d = getDefaultClassNames()
  const navButton = cn(
    buttonVariants({ variant: buttonVariant }),
    'h-8 w-8 select-none p-0 aria-disabled:opacity-50',
  )
  return {
    root: cn('w-fit', d.root),
    months: cn('relative flex flex-col gap-4 md:flex-row', d.months),
    month: cn('flex w-full flex-col gap-4', d.month),
    nav: cn(
      'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
      d.nav,
    ),
    button_previous: cn(navButton, d.button_previous),
    button_next: cn(navButton, d.button_next),
    month_caption: cn(
      'flex h-8 w-full items-center justify-center px-8',
      d.month_caption,
    ),
    dropdowns: cn(
      'flex h-8 w-full items-center justify-center gap-1.5 text-sm font-medium',
      d.dropdowns,
    ),
    dropdown_root: cn(
      'has-focus:border-ring border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-md border',
      d.dropdown_root,
    ),
    dropdown: cn('bg-popover absolute inset-0 opacity-0', d.dropdown),
    caption_label: cn(
      'select-none font-medium',
      captionLayout === 'label'
        ? 'text-sm'
        : '[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-2.5',
      d.caption_label,
    ),
    table: 'w-full border-collapse',
    weekdays: cn('flex', d.weekdays),
    weekday: cn(
      'text-muted-foreground flex-1 select-none rounded-md text-xs font-normal',
      d.weekday,
    ),
    week: cn('mt-2 flex w-full', d.week),
    week_number_header: cn('w-8 select-none', d.week_number_header),
    week_number: cn('text-muted-foreground select-none text-xs', d.week_number),
    day: cn(
      'group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
      d.day,
    ),
    range_start: cn('bg-accent rounded-l-md', d.range_start),
    range_middle: cn('rounded-none', d.range_middle),
    range_end: cn('bg-accent rounded-r-md', d.range_end),
    today: cn(
      'bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none',
      d.today,
    ),
    outside: cn(
      'text-muted-foreground aria-selected:text-muted-foreground',
      d.outside,
    ),
    disabled: cn('text-muted-foreground opacity-50', d.disabled),
    hidden: cn('invisible', d.hidden),
  }
}

function CalendarRoot({
  className: rootClassName,
  rootRef,
  ...rest
}: React.ComponentProps<'div'> & { rootRef?: React.Ref<HTMLDivElement> }) {
  return (
    <div
      data-slot="calendar"
      ref={rootRef}
      className={cn(rootClassName)}
      {...rest}
    />
  )
}

function CalendarChevron({
  className: chevronClassName,
  orientation,
  ...rest
}: React.ComponentProps<'svg'> & { orientation?: 'left' | 'right' | 'up' }) {
  if (orientation === 'left') {
    return (
      <ChevronLeftIcon className={cn('size-3', chevronClassName)} {...rest} />
    )
  }
  if (orientation === 'right') {
    return (
      <ChevronRightIcon className={cn('size-3', chevronClassName)} {...rest} />
    )
  }
  return (
    <ChevronDownIcon className={cn('size-2', chevronClassName)} {...rest} />
  )
}

function CalendarWeekNumber({ children, ...rest }: React.ComponentProps<'td'>) {
  return (
    <td {...rest}>
      <div className="flex size-8 items-center justify-center text-center">
        {children}
      </div>
    </td>
  )
}

const DEFAULT_COMPONENTS = {
  Root: CalendarRoot,
  Chevron: CalendarChevron,
  DayButton: CalendarDayButton,
  WeekNumber: CalendarWeekNumber,
} as unknown as React.ComponentProps<typeof DayPicker>['components']

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  formatters,
  components,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'bg-background group/calendar p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        ...buildClassNames(buttonVariant, captionLayout),
        ...classNames,
      }}
      components={{ ...DEFAULT_COMPONENTS, ...components }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  const focused = modifiers['focused']
  const selected = modifiers['selected']
  const rangeStart = modifiers['range_start']
  const rangeEnd = modifiers['range_end']
  const rangeMiddle = modifiers['range_middle']

  React.useEffect(() => {
    if (focused) ref.current?.focus()
  }, [focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        selected && !rangeStart && !rangeEnd && !rangeMiddle
      }
      data-range-start={rangeStart}
      data-range-end={rangeEnd}
      data-range-middle={rangeMiddle}
      className={cn(
        'data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 flex aspect-square h-auto w-full min-w-8 flex-col gap-1 font-normal leading-none data-[range-end=true]:rounded-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] [&>span]:text-xs [&>span]:opacity-70',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
